<script setup lang="ts">
import {computed, onBeforeMount, onMounted, onUnmounted} from 'vue';
import {useUxData} from "@/stores/ux-data";
import {useAppDataStore} from "@/stores/app-data";
import {usePredicateStore} from "@/stores/predicate-store";
import {useGuidedSetupStore} from "@/stores/guided-setup-store";
import type {SourceEntry} from "@/stores/guided-setup-store";
import {handleError} from "@/appdata/globals";
import SearchField from "@/components/atoms/SearchField.vue";
import Pagination from "@/components/data/Pagination.vue";
import {useSearchableList} from "@/composables/useSearchableList";
import type {WorldInterface} from "@/types/worlds";

const ux = useUxData();
const store = useAppDataStore();
const predicateStore = usePredicateStore();
const setupStore = useGuidedSetupStore();

const typeButtons = [
  {alias: 'all', labelKey: 'all'},
  {alias: 'abilities', labelKey: 'abilities'},
  {alias: 'ancestries', labelKey: 'ancestries'},
  {alias: 'frameworks', labelKey: 'characterFrameworks'},
  {alias: 'edges', labelKey: 'edges'},
  {alias: 'skills', labelKey: 'skills'},
  {alias: 'hindrances', labelKey: 'hindrances'},
  {alias: 'powers', labelKey: 'powers'},
  {alias: 'equipment', labelKey: 'gear'},
  {alias: 'tables', labelKey: 'tables'},
];

// The list is useSearchableList's: its search waits for the user to stop typing, only the latest
// request fills it — answers can land in any order — and `loading` stays on until that one lands,
// which is what the pager waits for. loadWorldOptions answers into the store's world list — a
// list every world surface shares — so the rows are taken from it as each answer lands, rather
// than read from it on every render: an answer that has been superseded still overwrites the
// store's list, and must not show here.
const { dataItems: worlds, loading, metadata, links, fetch: fetchWorlds, onNextPage, onPreviousPage, onSearch: onKeywordChange } =
    useSearchableList<WorldInterface>({
      keyword: computed({
        get: () => predicateStore.worldFilters.stringPartial,
        set: (value) => { predicateStore.worldFilters.stringPartial = value; },
      }),
      fetchPage: (url) => store.loadWorldOptions(store.system, url, predicateStore.worldPredicates, false)
          .then((page) => page ? { ...page, results: store.worlds ?? [] } : undefined)
          .catch((error) => {
            handleError(error)
          }),
    });

const getTypeLabel = (button: { alias: string; labelKey: string }) => {
  // 'all' and 'tables' are labeled under worlds.elements, not swade.setting.elements,
  // matching ElementImportTool's lookups.
  if (button.alias === 'all') {
    return ux.labels?.worlds?.elements?.all ?? 'All';
  }
  if (button.alias === 'tables') {
    return ux.labels?.worlds?.elements?.tables ?? 'Tables';
  }
  return ux.labels?.swade?.setting?.elements?.[button.labelKey] ?? button.labelKey;
};

const isSourceAdded = (world: WorldInterface): boolean => {
  return setupStore.selectedSources.some(s => s.world.uuid === world.uuid);
};

const isTypeSelected = (entry: SourceEntry, alias: string): boolean => {
  if (alias === 'all') {
    return entry.elementTypes.length === 0;
  }
  return entry.elementTypes.includes(alias);
};

const onTypeToggle = (entry: SourceEntry, alias: string) => {
  setupStore.toggleSourceType(entry.world, alias);
};

const onSourceClick = (world: WorldInterface) => {
  setupStore.toggleSource(world);
};

const onRemoveSource = (entry: SourceEntry) => {
  setupStore.removeSource(entry.world);
};

onBeforeMount(() => {
  if (store.system && store.system.uuid !== predicateStore.system?.uuid) {
    predicateStore.system = store.system;
  }
});

onMounted(() => {
  setupStore.validateStep(1, true);
  predicateStore.ownedOnly = false;
  // The search box starts empty, so the keyword does too: the slot is shared with the world list
  // and the world browsers, whose own searches write it.
  predicateStore.worldFilters.stringPartial = "";
  fetchWorlds();
});

onUnmounted(() => {
  predicateStore.worldFilters.stringPartial = "";
});

</script>

<template>
  <div class="world-sources-step">
    <div class="form-group mb-3">
      <label class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.world?.sourcesLabel"></label>

      <p class="form-text mt-0 mb-3" v-html="ux.labels?.guidedSetup?.world?.sourcesHelp"></p>

      <div class="alert alert-info mb-3">
        <span class="fa-light fa-info-circle me-2"></span>
        <span v-html="ux.labels?.guidedSetup?.world?.sourcesNote"></span>
      </div>

      <div class="data-list-bar d-flex justify-content-between align-items-center mb-2">
        <div class="keyword-search me-3 mt-auto flex-grow-1">
          <SearchField
              :placeholder="ux.labels?.placeholders?.search ?? 'Search...'"
              @keyword-change="onKeywordChange"
          />
        </div>
        <div class="pagination">
          <Pagination
              v-if="metadata.total"
              :start="metadata.start"
              :end="metadata.end"
              :total="metadata.total"
              :disable-next="links.next === null"
              :disable-previous="links.previous === null"
              :loading="loading"
              @next="onNextPage"
              @previous="onPreviousPage"
          />
        </div>
      </div>

      <div class="d-flex flex-column flex-lg-row gap-3">
        <div class="flex-grow-1">
          <table v-if="worlds.length > 0" class="table table-hover mb-0">
            <thead>
            <tr>
              <th class="text-start">{{ ux.labels?.worlds?.name ?? 'Name' }}</th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="world in worlds"
                :key="world.uuid"
                role="button"
                :class="{ 'table-active': isSourceAdded(world) }"
                @click="onSourceClick(world)"
            >
              <td class="text-start gap-2">
                <span v-if="isSourceAdded(world)" class="d-lg-inline d-none fa-light fa-check text-success"></span>
                {{ world.name }}
                <span v-if="isSourceAdded(world)" class="d-lg-none fa-light fa-check text-success"></span>
              </td>
            </tr>
            </tbody>
          </table>
          <div v-else class="text-center py-4 text-muted">
            {{ ux.labels?.worlds?.empty ?? 'No worlds found.' }}
          </div>
        </div>

        <div v-if="setupStore.selectedSources.length > 0" class="selected-sources flex-shrink-0" style="min-width: 280px;">
          <ul class="list-group list-group-flush">
            <li v-for="entry in setupStore.selectedSources" :key="entry.world.uuid" class="list-group-item">
              <div class="d-flex justify-content-between align-items-center">
                <span class="source-name fw-semibold">{{ entry.world.name }}</span>
                <button
                    type="button"
                    class="btn btn-sm btn-outline-danger rounded-pill"
                    @click="onRemoveSource(entry)"
                >
                  <span class="fa-light fa-times"></span>
                </button>
              </div>
              <div class="element-filters btn-group btn-group-sm flex-wrap mt-2">
                <button
                    v-for="button in typeButtons"
                    :key="button.alias"
                    type="button"
                    class="badge btn btn-sm fw-semibold"
                    :class="isTypeSelected(entry, button.alias) ? 'btn-secondary' : 'btn-primary'"
                    @click="onTypeToggle(entry, button.alias)"
                >
                  {{ getTypeLabel(button) }}
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
